import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ABSENCE_TYPES = [
  { value: 'Urlaub', label: 'Urlaub', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'Krank', label: 'Krank', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'Schule', label: 'Schule', color: 'bg-purple-100 text-purple-800 border-purple-300' }
];

export const AbsenceModal = ({ open, onClose, date, absence, workers, onSuccess }) => {
  const [formData, setFormData] = useState({
    worker_id: "",
    type: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (absence) {
      setFormData({
        worker_id: absence.worker_id,
        type: absence.type,
        notes: absence.notes || ""
      });
    } else {
      setFormData({ worker_id: "", type: "", notes: "" });
    }
  }, [absence]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.worker_id) { toast.error("Bitte einen Mitarbeiter auswählen"); return; }
    if (!formData.type) { toast.error("Bitte einen Abwesenheitsgrund auswählen"); return; }
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) { toast.error("Sitzung abgelaufen — bitte neu anmelden"); setLoading(false); return; }
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (absence) {
        await axios.put(`${API}/absences/${absence.id}`, formData, { headers });
        toast.success("Abwesenheit aktualisiert!");
      } else {
        await axios.post(`${API}/absences`, { ...formData, date }, { headers });
        toast.success("Abwesenheit erstellt!");
      }
      onSuccess();
      onClose();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 401) {
        toast.error("Sitzung abgelaufen — bitte neu anmelden");
      } else if (status === 403) {
        toast.error("Keine Berechtigung für diese Aktion");
      } else {
        toast.error(detail || `Fehler beim Speichern der Abwesenheit (${status || 'Netzwerkfehler'})`);
      }
      console.error("Absence save error:", status, detail, error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/absences/${absence.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Abwesenheit gelöscht!");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Fehler beim Löschen der Abwesenheit");
    }
    setConfirmDelete(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="absence-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            {absence ? "Abwesenheit bearbeiten" : "Abwesenheit hinzufügen"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Datum</Label>
            <div className="mt-1 px-3 py-2 bg-slate-50 rounded-md text-sm text-slate-700" data-testid="absence-date">
              {new Date(date + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>

          <div>
            <Label htmlFor="worker">Mitarbeiter *</Label>
            <Select
              value={formData.worker_id}
              onValueChange={(value) => setFormData({ ...formData, worker_id: value })}
              required
            >
              <SelectTrigger id="worker" data-testid="absence-worker-select">
                <SelectValue placeholder="Mitarbeiter auswählen" />
              </SelectTrigger>
              <SelectContent>
                {workers.map(worker => (
                  <SelectItem key={worker.id} value={worker.id} data-testid={`worker-option-${worker.id}`}>
                    {worker.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="type">Abwesenheitsgrund *</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value })}
              required
            >
              <SelectTrigger id="type" data-testid="absence-type-select">
                <SelectValue placeholder="Grund auswählen" />
              </SelectTrigger>
              <SelectContent>
                {ABSENCE_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value} data-testid={`absence-type-${type.value}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded ${type.color}`} />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional: Zusätzliche Informationen..."
              data-testid="absence-notes-textarea"
              rows={2}
            />
          </div>

          <div className="flex gap-2 justify-between pt-2">
            {absence && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                data-testid="delete-absence-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {confirmDelete ? "Bestätigen?" : "Löschen"}
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-absence-button">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="save-absence-button">
                {loading ? "Speichern..." : absence ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AbsenceModal;